import * as React from 'react'

import * as T from 'components/ui/typography'
import ChecmarkSquareIcon from 'public/icons/checkmark-square.svg'
import DuelIcon from 'public/icons/challenges.svg'
import SimulationIcon from 'public/icons/material.svg'
import BookIcon from 'public/icons/book.svg'

import { Sliders } from 'lucide-react'

import Router, { useRouter } from 'next/router'
import { PageFormat, subCategoryFormat } from './all-pages'
import useTranslation from 'next-translate/useTranslation'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible'
import { cn } from 'lib/utils'

import { ChevronDown, ChevronUp } from 'lucide-react'

interface NavigationListProps {
    pages: PageFormat[]
    pageSel?: number
    setPageSel: (val: number) => void
    actualMacrosection: string
}

const icons = [
    <ChecmarkSquareIcon viewBox="12 0 30 30" className="fill-inherit h-5  aspect-square" key="checkmark" />,
    <DuelIcon viewBox="0 -3 30 30" className="fill-inherit h-5 w-full aspect-square" key="duel" />,
    <SimulationIcon viewBox="0 -3 30 30" className="fill-inherit h-5 w-full aspect-square" key="sim" />,
    <BookIcon viewBox="3 -2 20 20" className="fill-inherit h-5 w-full aspect-square" key="sim" />,
    <Sliders viewBox="0 -4 30 30" className="text-inherit h-5 aspect-square" key="setting" />,
]

const NavigationList = ({ pages, pageSel, setPageSel, actualMacrosection }: NavigationListProps) => {
    const router = useRouter()

    const isMacroSelected = (path: string) => actualMacrosection === path

    const handleNavigation = (subCategory: subCategoryFormat, page: PageFormat) => {
        if (page.path === 'theory') Router.push(`/${page.path}/${subCategory.tabs[0]}`)
        else {
            //If there is a nested route available, direct the routing to the corresponding nested layout.
            if (subCategory.tabs.length > 1) Router.push(`/${page.path}/${subCategory.label}/${subCategory.tabs[0]}`)
            //routes to the page if nested route is not present
            else Router.push(`/${page.path}/${subCategory.label}`)
        }
    }

    const path = router.pathname.split('/')

    const { t } = useTranslation('common')
    return (
        <div className="flex flex-col gap-3">
            {pages.map((page, index) => (
                <Collapsible
                    key={page.name}
                    defaultOpen={isMacroSelected(page.path)}
                    open={pageSel === index}
                    className="flex flex-col gap-3">
                    <div className="w-full">
                        <CollapsibleTrigger
                            className="flex cursor-pointer justify-between w-full items-center px-2 rounded-sm hover:bg-neutral-200 py-2"
                            onClick={() => {
                                setPageSel(index)
                            }}>
                            <div
                                className={cn('aspect-square h-6 w-6 overflow-hidden text-primary  fill-primary', {
                                    '!text-neutral-400 !fill-neutral-400': pageSel !== index,
                                })}>
                                {icons[index]}
                            </div>
                            <div className="flex-1 flex items-start">
                                <p
                                    className={cn('font-regular text-sm capitalize', {
                                        'text-primary font-bold': pageSel === index,
                                    })}>
                                    {t(`${page.name.toLowerCase()}`)}
                                </p>
                            </div>
                            <div>{pageSel !== index ? <ChevronDown /> : <ChevronUp />}</div>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-1">
                            <div>
                                {page.subCategories.map((subCategory: subCategoryFormat) => {
                                    const selected = path.includes(subCategory.label) && isMacroSelected(page.path)
                                    return (
                                        <div
                                            key={subCategory.label}
                                            className={cn('pl-3 rounded-sm flex mt-1 justify-start hover:bg-accent', {
                                                'bg-accent': selected,
                                            })}
                                            onClick={() => handleNavigation(subCategory, page)}>
                                            <div className="flex cursor-pointer items-center">
                                                <span
                                                    className={cn(
                                                        {
                                                            'bg-secondary': selected,
                                                        },
                                                        'h-3/4 w-[4px] rounded-sm ml-2',
                                                    )}
                                                />

                                                <T.P className="capitalize pl-2 py-1 text-sm">
                                                    {t(`${subCategory.label.toLowerCase()}`)}
                                                </T.P>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </CollapsibleContent>
                    </div>
                </Collapsible>
            ))}
        </div>
    )
}

export default NavigationList
